import type { GatingPresetId } from "../builder/use-gating-form-builder";
import type {
  AuthorizationRuleForGatingInput,
  AuthorizationRuleGroupForGatingInput,
} from "../use-gating-rule-conversion";
import type {
  CriterionApplyContext,
  GatingCriterionTypeDefinition,
} from "./gating-criterion-type";
import { pointsCriterionType } from "./points.criterion-type";
import { productCriterionType } from "./product.criterion-type";
import { tokenCriterionType } from "./token.criterion-type";
import { membershipCriterionType } from "./membership.criterion-type";
import type { GatingStrategyType } from "../strategies/gating-strategy";

// ---------------------------------------------------------------------------
// Registry (ordered array — detection order matters: points > product > token > membership)
// ---------------------------------------------------------------------------

export const CRITERION_TYPE_REGISTRY: readonly GatingCriterionTypeDefinition[] =
  [
    pointsCriterionType,
    productCriterionType,
    tokenCriterionType,
    membershipCriterionType,
  ];

// ---------------------------------------------------------------------------
// Lookup maps (derived from registry, computed once)
// ---------------------------------------------------------------------------

export const CRITERION_TYPE_BY_PRESET = new Map<
  GatingPresetId,
  GatingCriterionTypeDefinition
>(CRITERION_TYPE_REGISTRY.map((t) => [t.presetId, t]));

export const CRITERION_TYPE_BY_KEY = new Map<
  GatingStrategyType,
  GatingCriterionTypeDefinition
>(CRITERION_TYPE_REGISTRY.map((t) => [t.classificationKey, t]));

// ---------------------------------------------------------------------------
// Helper functions (replace switch statements in composables)
// ---------------------------------------------------------------------------

/**
 * Classify a single rule by its active criterion type.
 * Returns the classificationKey of the first matching type, or "none".
 * Detection order follows the registry order (points > product > token > membership).
 */
export function classifyRule(rule: AuthorizationRuleForGatingInput): string {
  for (const typeDef of CRITERION_TYPE_REGISTRY) {
    if (typeDef.hasCriterion(rule)) {
      return typeDef.classificationKey;
    }
  }
  return "none";
}

/**
 * Apply a criterion-type preset to all rules in a draft.
 * Clears all other criteria and applies defaults for the selected type.
 */
export function applyPresetToRules(
  draft: AuthorizationRuleGroupForGatingInput,
  typeDef: GatingCriterionTypeDefinition,
  context: CriterionApplyContext,
): void {
  for (const rule of draft.authorization_rules) {
    clearAllCriteria(rule);
    typeDef.applyDefaults(rule, context);
  }
}

/**
 * Returns true if all rules have identical criteria across every registered type.
 */
export function allCriteriaEqual(
  config: AuthorizationRuleGroupForGatingInput,
): boolean {
  const rules = config.authorization_rules;
  if (rules.length <= 1) return true;

  const first = rules[0]!;
  return rules.every((r) =>
    CRITERION_TYPE_REGISTRY.every((t) => t.criterionEquals(first, r)),
  );
}

/**
 * Copy all criteria from the first rule to every subsequent rule (deep clone).
 */
export function copyAllCriteria(
  draft: AuthorizationRuleGroupForGatingInput,
): void {
  const source = draft.authorization_rules[0];
  if (!source) return;

  for (let i = 1; i < draft.authorization_rules.length; i++) {
    const target = draft.authorization_rules[i]!;
    const { authorization_rule_id, significant_permissions_mask } = target;
    for (const typeDef of CRITERION_TYPE_REGISTRY) {
      typeDef.copyCriterion(source, target);
    }
    target.authorization_rule_id = authorization_rule_id;
    target.significant_permissions_mask = significant_permissions_mask;
  }
}

/**
 * Clear every criterion on a single rule.
 */
export function clearAllCriteria(rule: AuthorizationRuleForGatingInput): void {
  for (const typeDef of CRITERION_TYPE_REGISTRY) {
    typeDef.clearCriterion(rule);
  }
}

/**
 * Returns true if any registered criterion type considers this rule gated.
 */
export function isRuleGatedByAnyType(
  rule: AuthorizationRuleForGatingInput,
  freeMembershipId: string | undefined,
): boolean {
  return CRITERION_TYPE_REGISTRY.some((t) => t.isGated(rule, freeMembershipId));
}

/**
 * Serialize all criteria into a single object for change detection.
 */
export function serializeAllCriteria(
  rule: AuthorizationRuleForGatingInput,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const typeDef of CRITERION_TYPE_REGISTRY) {
    result[typeDef.classificationKey] = typeDef.serializeCriterion(rule);
  }
  return result;
}
